import { useState, useEffect, useMemo, useCallback } from "react";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import "./customStyle.css";
import { LoadingOutlined } from "@ant-design/icons";
import {
  useUpdateLessonCompleteStatusMutation,
  LessonFragmentDoc,
  useGetUnitLazyQuery,
  useHeaGetUnitInfoLazyQuery,
  useHeaUpdateLessonStatusMutation,
  StudyPlanContentFragment,
  useFeedbackFormLazyQuery,
  useCancelFeedbackFormMutation,
  Content,
} from "@api/graphql-codegen";
import styles from "./LessonForm.module.scss";
import { ExitTicketActive, Lesson, LessonStatus } from "../../../types";
import NormalCheckIcon from "../../../assets/icons/mark_check_normal.svg?react";
import MarkCheckIcon from "../../../assets/icons/mark_check.svg?react";
import InprogressIcon from "../../../assets/icons/mark_inprogress.svg?react";
import MarkCrossIcon from "src/assets/icons/cross_circle_filled.svg?react";
import ConnectIcon from "src/assets/icons/arrow_connect.svg?react";
import CompleteConnectIcon from "src/assets/icons/arrow_connect_complete.svg?react";
import MisconnectIcon from "src/assets/icons/arrow_misconnect.svg?react";
import InCompleteConnectIcon from "src/assets/icons/arrow_misconnect_incomplete.svg?react";
import DisconnectIcon from "src/assets/icons/arrow_prerequisite.svg?react";
import BurgerIcon from "src/assets/icons/burger.svg?react";
import TicketIcon from "src/assets/icons/ticket.svg?react";
import OutlineIcon from "src/assets/icons/outline.svg?react";
import WarningIcon from "src/assets/icons/warning-red1.svg?react";
import ShareIcon from "src/assets/icons/new-share.svg?react";
import IconOutline from "src/assets/icons/new-outline-info.svg?react";
import IconArrowUp from "src/assets/icons/dropdown-white.svg?react";
import { getMediaAbsolutePath } from "../../../services/File";
import Button from "@src/components/button/Button";
import { useToast } from "@hooks/useToast";
import {
  useAnalytics,
  Events,
  useCourseContent,
  useExitTicketLocalStorage,
} from "@hooks";
import {
  IconInfo,
  IconRateLesson,
  SURVEY_TYPE,
  CreatingResilientClassroomsId,
  CreatingResilientAusClassroomsId,
  isHomeEDApp,
} from "@constants";
import { resolveUnitMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import {
  resolveMixpanelProgramName,
  resolveMixpanelUnitGrade,
  mixpanelMultiCollectionProps,
} from "@src/utils/mixpanelProgramType";
import { mixpanelCompetencyProps } from "@src/utils/mixpanelCompetency";
import { mixpanelSkillProps } from "@src/utils/mixpanelSkill";
import { mixpanelMoodProps } from "@src/utils/mixpanelMood";
import Tooltip from "antd/es/tooltip";
import Spinner from "@src/components/spinner/Spinner";
import { Menu } from "antd";
import { CopyToClipboard, Collapse } from "@src/utils/componentCasts";
// CopyToClipboard typing is strict and TS sometimes rejects it as a JSX component;
// use the same casting approach as in `LessonForm.tsx`.
const CopyToClipboardComponent =
  CopyToClipboard as unknown as React.ComponentType<any>;
import MaterialCard from "@src/components/materialCard/Card";
import { useLessonFeedbackStatusStorage } from "@src/hooks/useLessonFeedbackStatusStorage";
import { LordIcon } from "@src/components/lord-icon/lord-icon";
import { hasArrayValues } from "@src/utils/helpers";
import { logger } from "../../../utils/logger";

type Props = {
  lesson: Lesson;
  index: number;
  lessonCount: number;
  preLesson?: Lesson;
  nextLesson?: Lesson;
  openDetails: (lesson: Lesson) => void;
  openPreview: (lesson: Lesson) => void;
  openTeacherFeedback: (lesson: Lesson) => void;
  unitId: string;
  lessonInfo: {
    unit_name: string | undefined;
    unit_id: string | undefined;
    course_name: string | undefined;
    course_id: string | undefined;
    lesson_name: string | undefined;
    lesson_id: string | undefined;
    lesson_position: number | 0;
    grade?: string;
    competency?: string;
    skill?: string;
    schoolId: string | undefined;
    schoolName: string | undefined;
    userId: string | undefined;
    userName: string | undefined;
  };
  isParentApp?: boolean;
  handleGenerateTicket?: (lesson: Lesson) => void;
  handleCancelExitTicket?: (lesson: Lesson) => void;
  exitTicket?: ExitTicketActive;
  movedScroll?: boolean;
  isOnlyNotCompletedLesson?: boolean;
  preRequiredLessonNumbers?: number[];
  incompleted?: boolean;
  openExitTicket?: (
    lesson: Lesson,
    studyPlan: StudyPlanContentFragment,
  ) => void;
  handleResumeMenu?: (lesson: Lesson) => void;
  handleStartBeginMenu?: (lesson: Lesson) => void;
  unitUniqueName?: string | null;
  handleDownloadMaterial?: (
    content: Content,
    lesson: Lesson,
    needPrint?: boolean,
  ) => void;
  showMaterailView?: boolean;
  handleOpenShareModal: (lesson: Lesson) => void;
  studyPlan?: StudyPlanContentFragment;
  getHeaLessonCompletedStatus?: () => void;
  getHeaLessonStatusLoading?: boolean;
};

const createURL = (id: string) => {
  return `${window.location.origin}/ff/${id}`;
};

function NewLayoutLessonForm(props: Props) {
  const {
    lesson,
    index,
    lessonCount,
    preLesson,
    nextLesson,
    openDetails,
    openPreview,
    openTeacherFeedback,
    unitId,
    lessonInfo,
    isParentApp,
    handleGenerateTicket,
    handleCancelExitTicket,
    exitTicket,
    movedScroll,
    isOnlyNotCompletedLesson,
    preRequiredLessonNumbers,
    incompleted,
    handleResumeMenu,
    handleStartBeginMenu,
    handleDownloadMaterial,
    showMaterailView,
    handleOpenShareModal,
    studyPlan,
    getHeaLessonCompletedStatus,
    getHeaLessonStatusLoading,
  } = props;
  const { successToast } = useToast();
  const {
    name,
    image,
    number,
    duration,
    descriptionHeading,
    descriptionAnswers,
    prerequisite,
    status,
    progress,
    sensitive,
    sensitiveTooltip,
  } = lesson;

  const [box, setBox] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const [completeRequire, setCompleteRequire] = useState(false);
  const [complete, setComplete] = useState(false);
  const [inprogress, setInprogress] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showExitTicketMenu, setShowExitTicketMenu] = useState(false);

  const [selectMaterial, setSelectMaterial] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState<string | undefined>(
    undefined,
  );

  const { trackEvent } = useAnalytics();
  const {
    exitTickets,
    setExitTickets,
    currentUnit,
    lesson: completeLesson,
    isNewLessonLayout,
    setLesson,
  } = useCourseContent();
  const { getFormId, removeFormId } = useExitTicketLocalStorage();
  const {
    getFeedbackStatusWithLesson,
    setFeedbackStatusWithLesson,
    removeFeedbackStatusWithLesson,
  } = useLessonFeedbackStatusStorage();
  const [rateHover, setRateHover] = useState(false);

  const isRequire = useMemo(
    () => !!preRequiredLessonNumbers?.length,
    [preRequiredLessonNumbers?.length],
  );
  const [feedbackForm, { loading: feedbackLoading }] = useFeedbackFormLazyQuery(
    {
      fetchPolicy: "network-only",
      onCompleted(res) {
        if (res.feedbackForm) {
          const tickets = exitTickets.map((ticket) => {
            if (number === ticket.lessonNum) {
              const currentTime = new Date().getTime();
              const expiration = parseInt(
                res.feedbackForm?.expirationAt ?? "0",
              );
              const url = createURL(res.feedbackForm!.link ?? "");
              const ticketCount = res.feedbackForm?.exitTicketCount ?? 0;
              const expiryMins = (expiration - currentTime) / 1000;

              if (expiryMins > 0) {
                return {
                  ...ticket,
                  status: true,
                  exitTicketCount: ticketCount,
                  expiryMinutes: expiryMins,
                  copyLink: url,
                  code: res.feedbackForm?.link ?? "",
                };
              } else {
                return {
                  ...ticket,
                  status: false,
                  exitTicketCount: 0,
                  expiryMinutes: 0,
                  copyLink: "",
                  code: "",
                };
              }
            }
            return ticket;
          });
          setExitTickets(tickets);
        }
      },
      onError(e) {
        const errorMessage = e?.message || e?.graphQLErrors?.[0]?.message;
        if (errorMessage?.includes("QR code already expired.")) {
          removeFormId(lesson.id);
          const tickets = exitTickets.map((ticket) => {
            if (lesson?.number === ticket.lessonNum) {
              return {
                ...ticket,
                status: false,
                exitTicketCount: 0,
                expiryMinutes: 0,
                copyLink: "",
                code: "",
              };
            }
            return ticket;
          });
          setExitTickets(tickets);
        }
      },
    },
  );
  const formId = getFormId(lesson.id);

  useEffect(() => {
    if (showMaterailView && completeLesson) {
      // Selected Lesson
      setSelectMaterial(true);
      setSelectedLessonId(completeLesson.id);
    }
  }, [showMaterailView, completeLesson]);

  useEffect(() => {
    if (formId) {
      feedbackForm({
        variables: {
          input: { id: formId },
        },
      });
    }
  }, [formId, feedbackForm]);

  useEffect(() => {
    if (status === LessonStatus.Completed) {
      setComplete(true);
    } else if (status === LessonStatus.Confirmed) {
      setComplete(true);
    } else if (status === LessonStatus.InProgress) {
      setInprogress(true);
    } else {
      setComplete(false);
      setInprogress(false);
    }
  }, [status]);

  useEffect(() => {
    if (movedScroll) {
      setShowMenu(false);
      setShowExitTicketMenu(false);
    }
  }, [movedScroll]);

  const [getUnitInfo, { loading: fetching }] = useGetUnitLazyQuery({
    fetchPolicy: "network-only",
  });
  const [heaGetUnitInfo, { loading: fetchingHea }] = useHeaGetUnitInfoLazyQuery(
    { fetchPolicy: "network-only" },
  );
  const [updateLessonComplete, { loading }] =
    useUpdateLessonCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      onCompleted() {
        if (isHomeEDApp) {
          heaGetUnitInfo({
            variables: { input: { id: unitId } },
          });
        } else {
          getUnitInfo({
            variables: { input: { id: unitId } },
          });
        }
      },
      update(cache, res, { variables }) {
        const d = res.data?.updateLessonCompleteStatus;
        if (d && lesson?.id) {
          cache.updateFragment(
            {
              id: `Lesson:${lesson?.id}`,
              fragment: LessonFragmentDoc,
              fragmentName: "Lesson",
            },
            (l) => ({
              ...l,
              status: variables?.input.status,
            }),
          );
          if (variables?.input.status === LessonStatus.Completed) {
            if (
              lessonInfo.unit_name === "Educator Onboarding" ||
              lessonInfo.unit_name === "Educator Wellbeing"
            ) {
              const courseName =
                lessonInfo?.unit_name === "Educator Onboarding"
                  ? "Educator Onboarding"
                  : lessonInfo?.unit_name === "Educator Wellbeing"
                    ? "Educator Wellbeing"
                    : "";
              const resolvedProgramName = resolveMixpanelProgramName({
                courseName: courseName || lessonInfo?.course_name,
                unitName: lessonInfo?.unit_name,
                unitId: currentUnit?.id,
                unitUniqueName: currentUnit?.uniqueName,
              });
              const updatedTrackData = {
                ...lessonInfo,
                course_name: courseName,
                grade: resolveMixpanelUnitGrade({
                  courseName: courseName || lessonInfo?.course_name,
                  grade: lessonInfo?.grade,
                }),
                ...mixpanelCompetencyProps(
                  lessonInfo?.competency || currentUnit?.competency,
                ),
                ...mixpanelSkillProps({
                  skill: lessonInfo?.skill || currentUnit?.skill,
                }),
                ...mixpanelMoodProps({
                  categories: isHomeEDApp ? undefined : currentUnit?.categories,
                }),
                ...mixpanelMultiCollectionProps(),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: lessonInfo?.unit_name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                program_name: resolvedProgramName,
              };
              trackEvent(Events.actions.lessonCompleted, {
                ...updatedTrackData,
                type: "Unit",
              });
            } else {
              const resolvedProgramName = resolveMixpanelProgramName({
                courseName: lessonInfo?.course_name,
                unitName: lessonInfo?.unit_name,
                unitId: currentUnit?.id,
                unitUniqueName: currentUnit?.uniqueName,
              });
              trackEvent(Events.actions.lessonCompleted, {
                ...lessonInfo,
                grade: resolveMixpanelUnitGrade({
                  courseName: lessonInfo?.course_name,
                  grade: lessonInfo?.grade,
                }),
                ...mixpanelCompetencyProps(
                  lessonInfo?.competency || currentUnit?.competency,
                ),
                ...mixpanelSkillProps({
                  skill: lessonInfo?.skill || currentUnit?.skill,
                }),
                ...mixpanelMoodProps({
                  categories: isHomeEDApp ? undefined : currentUnit?.categories,
                }),
                ...mixpanelMultiCollectionProps(),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                program_name: resolvedProgramName,
                type: "Unit",
              });
            }
            if (isOnlyNotCompletedLesson) {
              trackEvent(Events.actions.allLessonsCompletedInUnit, {
                ...lessonInfo,
                grade: resolveMixpanelUnitGrade({
                  courseName: lessonInfo?.course_name,
                  grade: lessonInfo?.grade,
                }),
                ...mixpanelCompetencyProps(
                  lessonInfo?.competency || currentUnit?.competency,
                ),
                ...mixpanelSkillProps({
                  skill: lessonInfo?.skill || currentUnit?.skill,
                }),
                ...mixpanelMoodProps({
                  categories: isHomeEDApp ? undefined : currentUnit?.categories,
                }),
                ...mixpanelMultiCollectionProps(),
                program_name: resolveMixpanelProgramName({
                  courseName: lessonInfo?.course_name,
                  unitName: lessonInfo?.unit_name || currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                type: "Unit",
              });
            } else if (currentUnit && !currentUnit?.progress) {
              trackEvent(Events.actions.startUnit, {
                ...lessonInfo,
                grade: resolveMixpanelUnitGrade({
                  courseName: lessonInfo?.course_name,
                  grade: lessonInfo?.grade,
                }),
                ...mixpanelCompetencyProps(
                  lessonInfo?.competency || currentUnit?.competency,
                ),
                ...mixpanelSkillProps({
                  skill: lessonInfo?.skill || currentUnit?.skill,
                }),
                ...mixpanelMoodProps({
                  categories: isHomeEDApp ? undefined : currentUnit?.categories,
                }),
                ...mixpanelMultiCollectionProps(),
                program_name: resolveMixpanelProgramName({
                  courseName: lessonInfo?.course_name,
                  unitName: lessonInfo?.unit_name || currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                type: "Unit",
              });
            }
          }
        }
      },
    });

  const [updateHeaLessonComplete, { loading: heaLessonCompleteInProgress }] =
    useHeaUpdateLessonStatusMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      update(_, __, { variables }) {
        if (isHomeEDApp) {
          if (
            variables?.input.status === LessonStatus.Completed ||
            variables?.input.status === LessonStatus.Confirmed
          ) {
            const resolvedProgramName = resolveMixpanelProgramName({
              courseName: lessonInfo?.course_name,
              unitName: currentUnit?.name,
              unitId: currentUnit?.id,
              unitUniqueName: currentUnit?.uniqueName,
            });
            trackEvent(Events.actions.lessonCompleted, {
              ...lessonInfo,
              program_name: resolvedProgramName,
              grade: resolveMixpanelUnitGrade({
                courseName: lessonInfo?.course_name,
                grade: lessonInfo?.grade,
              }),
              ...mixpanelCompetencyProps(
                lessonInfo?.competency || currentUnit?.competency,
              ),
              ...mixpanelSkillProps({
                skill: lessonInfo?.skill || currentUnit?.skill,
              }),
              ...mixpanelMoodProps({
                categories: isHomeEDApp ? undefined : currentUnit?.categories,
              }),
              ...mixpanelMultiCollectionProps(),
              lessonType: resolveUnitMixpanelLessonType({
                unitName: currentUnit?.name,
                unitId: currentUnit?.id,
                unitUniqueName: currentUnit?.uniqueName,
              }),
              study_plan_id: studyPlan?.id,
              study_plan_name: studyPlan?.name,
              type: "Unit",
            });
            if (isOnlyNotCompletedLesson) {
              trackEvent(Events.actions.allLessonsCompletedInUnit, {
                ...lessonInfo,
                study_plan_id: studyPlan?.id,
                study_plan_name: studyPlan?.name,
                grade: resolveMixpanelUnitGrade({
                  courseName: lessonInfo?.course_name,
                  grade: lessonInfo?.grade,
                }),
                ...mixpanelCompetencyProps(
                  lessonInfo?.competency || currentUnit?.competency,
                ),
                ...mixpanelSkillProps({
                  skill: lessonInfo?.skill || currentUnit?.skill,
                }),
                ...mixpanelMoodProps({
                  categories: isHomeEDApp ? undefined : currentUnit?.categories,
                }),
                ...mixpanelMultiCollectionProps(),
                program_name: resolveMixpanelProgramName({
                  courseName: lessonInfo?.course_name,
                  unitName: currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: currentUnit?.name,
                  unitId: currentUnit?.id,
                  unitUniqueName: currentUnit?.uniqueName,
                }),
                type: "Unit",
              });
            }
          }
        }
      },
    });

  const [cancelFeedbackForm, { loading: canceling }] =
    useCancelFeedbackFormMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      onCompleted(data) {
        if (data.cancelFeedbackForm) {
          removeFormId(lesson.id);
          const tickets = exitTickets.map((ticket) => {
            if (lesson?.number === ticket.lessonNum) {
              return {
                ...ticket,
                status: false,
                exitTicketCount: 0,
                expiryMinutes: 0,
                copyLink: "",
                code: "",
              };
            }
            return ticket;
          });
          setExitTickets(tickets);
        }
      },
    });
  useEffect(() => {
    if (descriptionAnswers) {
      setBox(JSON.parse(descriptionAnswers || "[]"));
    }
  }, [descriptionAnswers]);

  const preLessonNum = useMemo(() => preLesson?.number, [preLesson?.number]);

  const handleStartLesson = () => {
    setShowMenu(false);
    setShowExitTicketMenu(false);
    // setShowExitTicketActiveMenu(false);
    openDetails(lesson);
  };
  const handleResumeClick = () => {
    setLesson(lesson);
    setShowMenu(false);
    setShowExitTicketMenu(false);
    handleResumeMenu?.(lesson);
  };

  const handleStartBeginClick = () => {
    setLesson(lesson);
    setShowMenu(false);
    setShowExitTicketMenu(false);
    handleStartBeginMenu?.(lesson);
  };

  const handleOpenFeedback = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowMenu(false);
    setShowExitTicketMenu(false);
    // setShowExitTicketActiveMenu(false);
    openTeacherFeedback(lesson);
    setFeedbackStatusWithLesson(lesson.id, "Hidden");
  };

  const items: MenuProps["items"] =
    inprogress && progress && progress > 0
      ? [
          {
            key: "1",
            label: (
              <div
                className={styles.dropDownResumnMenuitem}
                onClick={handleResumeClick}
              >
                <div className={styles.resumeMenuItem}>
                  <div className={styles.resumeProgressBackBar}>
                    <div
                      className={styles.resumeProgressBar}
                      style={{
                        width: `${complete ? (completeRequire ? 0 : 100) : completeRequire ? 100 : inprogress ? progress : 0}%`,
                        backgroundColor: complete ? "#62C590" : "#62C590",
                        borderBottomRightRadius: complete ? "3px" : "0",
                      }}
                    ></div>
                    <span className={styles.inprogressText}>
                      Lesson in Progress
                    </span>
                  </div>
                  <div className={styles.resumeText}>Resume Lesson</div>
                </div>
              </div>
            ),
          },
          {
            key: "2",
            label: (
              <div
                className={styles.dropDownMenuitem}
                onClick={handleStartBeginClick}
              >
                Start from Beginning
              </div>
            ),
          },
        ]
      : [
          {
            key: "1",
            label: (
              <div
                className={styles.dropDownMenuitem}
                onClick={handleStartLesson}
              >
                Start Lesson
              </div>
            ),
          },
        ];

  const generateExitTicket = (e: any) => {
    setShowExitTicketMenu(false);
    // setShowExitTicketActiveMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    if (handleGenerateTicket) {
      handleGenerateTicket(lesson);
    }
  };

  const cancelExitTicket = (e: any) => {
    setShowExitTicketMenu(false);
    // setShowExitTicketActiveMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    if (handleCancelExitTicket) {
      const formId = getFormId(lesson.id);
      if (formId) {
        cancelFeedbackForm({
          variables: {
            input: { id: formId },
          },
        });
      }
    }
  };

  const copyToClipboard = (e: any) => {
    setShowExitTicketMenu(false);
    // setShowExitTicketActiveMenu(false);
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    successToast("Link Copied Successfully!");
  };

  useEffect(() => {
    if (exitTicket?.expiryMinutes === 0 && exitTicket?.status) {
      if (handleCancelExitTicket) {
        const formId = getFormId(lesson.id);
        if (formId) {
          cancelFeedbackForm({
            variables: {
              input: { id: formId },
            },
          });
        }
      }
    }
  }, [
    exitTicket,
    lesson,
    handleCancelExitTicket,
    cancelFeedbackForm,
    getFormId,
  ]);

  const ticketActiveMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div
          className={styles.activeInfoMenu}
          onClick={(e) => e.stopPropagation()}
        >
          <OutlineIcon width={21} className={styles.warningIcon} />
          <div className={styles.activeInfo}>
            <span>
              <strong>
                {Math.round((exitTicket?.expiryMinutes ?? 0) / 60)}
              </strong>{" "}
              Minutes Left
            </span>
            <span>
              <strong>{exitTicket?.exitTicketCount}</strong> Responses Max
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>Cancel</div>,
      onClick: (e) => cancelExitTicket(e),
    },
    {
      key: "3",
      label: (
        <CopyToClipboardComponent text={exitTicket?.copyLink ?? ""}>
          <div className={styles.dropDownMenuitem}>Copy Link</div>
        </CopyToClipboardComponent>
      ),
      onClick: (e) => copyToClipboard(e),
    },
    {
      key: "4",
      label: <div className={styles.dropDownMenuitem}>Regenerate</div>,
      onClick: (e) => generateExitTicket(e),
    },
  ];

  const handleClickActiveMenu = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
  };

  const activeSurveyTitle = (type: Number) => {
    switch (type) {
      case SURVEY_TYPE.RESEARCH: {
        return "Research Survey Active";
      }
      case SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ: {
        return "Pre-Program Active";
      }
      case SURVEY_TYPE.EXIT_TICKET: {
        return "Exit Ticket Active";
      }
      case SURVEY_TYPE.NA_POST_PROGRAM_QUIZ: {
        return "Post-Program Active";
      }
      default: {
        return "Research Survey Active";
      }
    }
  };

  const generateSurveyTitle = (type: Number) => {
    switch (type) {
      case SURVEY_TYPE.RESEARCH: {
        return "Generate Survey Link";
      }
      case SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ: {
        return "Generate Pre-Program Quiz";
      }
      case SURVEY_TYPE.EXIT_TICKET: {
        return "Generate Exit Ticket";
      }
      case SURVEY_TYPE.NA_POST_PROGRAM_QUIZ: {
        return "Generate Post-Program Quiz";
      }
      default: {
        return "Generate Survey Link";
      }
    }
  };

  const normalMenu: MenuProps["items"] = [
    {
      key: "3",
      label: <div className={styles.dropDownMenuitem}>{"Rate Lesson"}</div>,
      onClick: (e) => handleOpenFeedback(e),
    },
  ];
  const ticketActivatedMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Dropdown
          menu={{ items: ticketActiveMenu }}
          trigger={["hover"]}
          placement="top"
        >
          <div className={styles.exitTicketActiveButton}>
            {exitTicket?.type ? activeSurveyTitle(exitTicket?.type) : ""}
          </div>
        </Dropdown>
      ),
      onClick: (e) => handleClickActiveMenu(e),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>{"Rate Lesson"}</div>,
      onClick: (e) => handleOpenFeedback(e),
    },
  ];
  const ticketGenerateMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div className={styles.dropDownMenuitem}>
          {exitTicket?.type ? generateSurveyTitle(exitTicket?.type) : ""}
        </div>
      ),
      onClick: (e) => generateExitTicket(e),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>{"Rate Lesson"}</div>,
      onClick: (e) => handleOpenFeedback(e),
    },
  ];

  const lessonNumStyle = useMemo(() => {
    const classes = [styles.lessonNum];

    if (completeRequire) {
      if (complete) {
        classes.push(styles.incompleteBorder);
      } else {
        classes.push(styles.completeNumBorder);
      }
    }
    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestNumBorder);
      } else {
        classes.push(styles.hoverBorder);
      }
    }
    return classes.join(" ");
  }, [completeRequire, complete, isHovered, isRequire]);

  const topBorderStyle = useMemo(() => {
    const classes = [styles.cardTopBorderView];
    if (completeRequire) {
      if (complete) {
        classes.push(styles.incompleteBorder);
      } else {
        classes.push(styles.completeTopBorder);
      }
    }
    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestBorder);
      } else {
        classes.push(styles.hoverBorder);
      }
    }

    return classes.join(" ");
  }, [completeRequire, complete, isHovered, isRequire]);

  const bottomBorderStyle = useMemo(() => {
    const classes = [styles.cardBottomBorderView];
    if (completeRequire) {
      if (complete) {
        classes.push(styles.incompleteBorder);
      } else {
        classes.push(styles.completeBottomBorder);
      }
    }
    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestBorder);
      } else {
        classes.push(styles.hoverBorder);
      }
    }

    return classes.join(" ");
  }, [completeRequire, complete, isHovered, isRequire]);

  const preRequestStyle = useMemo(() => {
    const classes = [styles.preRequestSubView];
    if (completeRequire) {
      if (complete) {
        classes.push(styles.incompleteBorder);
      } else {
        classes.push(styles.completeTopBorder);
      }
    }
    return classes.join(" ");
  }, [completeRequire, complete]);

  const imageStyle = useMemo(() => {
    const classes = [styles.image];
    if (completeRequire) {
      classes.push(styles.hidden);
    }
    return classes.join(" ");
  }, [completeRequire]);

  const completedCheckStyle = useMemo(() => {
    const classes = [styles.completedCheck];
    if (completeRequire) {
      if (complete) {
        classes.push(styles.normalBorder);
      } else {
        classes.push(styles.completeTopBorder);
      }
    }
    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestBorder);
      } else {
        classes.push(styles.hoverBorder);
      }
    }
    return classes.join(" ");
  }, [completeRequire, complete, isHovered, isRequire]);

  const burgerButtonStyle = useMemo(() => {
    const classes = [styles.burgerButton];
    if (complete) {
      classes.push(styles.completeBurgerButton);
    }

    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestBurgerButton);
      }
    }

    if (feedbackLoading || canceling) {
      classes.push(styles.loadingBurgerButton);
    }
    return classes.join(" ");
  }, [complete, isHovered, isRequire, feedbackLoading, canceling]);

  const shareButtonStyle = useMemo(() => {
    const classes = [styles.shareButton];
    if (complete) {
      classes.push(styles.completeBurgerButton);
    }

    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestBurgerButton);
      }
    }

    if (feedbackLoading || canceling) {
      classes.push(styles.loadingBurgerButton);
    }
    return classes.join(" ");
  }, [complete, isHovered, isRequire, feedbackLoading, canceling]);

  const completedPathStyle = useMemo(() => {
    const classes = [styles.inCompleteConnectView];
    if (isRequire) {
      classes.push(styles.completeSuggestConnectPath);
    } else {
      classes.push(styles.completeConnectPath);
    }

    return classes.join(" ");
  }, [isRequire]);

  const materialViewStyle = useMemo(() => {
    const classes = [styles.materialSubView];

    if (completeRequire) {
      if (complete) {
        classes.push(styles.incompleteBorder);
      } else {
        classes.push(styles.completeTopBorder);
      }
    }
    if (isHovered) {
      if (isRequire) {
        classes.push(styles.suggestBorder);
      } else {
        classes.push(styles.hoverBorder);
      }
    }
    if (!selectMaterial) {
      classes.push(styles.noborder);
    }
    return classes.join(" ");
  }, [completeRequire, complete, isHovered, isRequire, selectMaterial]);

  const feedbackButtonStyle = useMemo(() => {
    const enabledFeedback = getFeedbackStatusWithLesson(lesson.id);
    const classes = [styles.feedbackButton];

    if (enabledFeedback === "Enable") {
      classes.push(styles.pulseAnimation);
    } else if (enabledFeedback === "Hidden") {
      classes.push(styles.feedBackHidden);
    }
    return classes.join(" ");
  }, [getFeedbackStatusWithLesson, lesson.id]);

  const burgeritems: MenuProps["items"] = [
    {
      label: "",
      key: "SubMenu",
      icon: (
        <Button
          disabled={canceling || feedbackLoading}
          label=""
          size="small"
          className={burgerButtonStyle}
          onClick={(e) => e.stopPropagation()}
          leftIcon={
            canceling || feedbackLoading ? (
              <Spinner width={20} height={20} />
            ) : (
              <BurgerIcon />
            )
          }
        />
      ),
      children: exitTicket?.formData
        ? exitTicket?.status
          ? ticketActivatedMenu
          : ticketGenerateMenu
        : normalMenu,
    },
  ];

  const handleHover = useCallback((status: boolean) => {
    setIsHovered(status);
  }, []);

  const handleSelectMaterials = useCallback(
    (e: any) => {
      if (e.domEvent) {
        e.domEvent.stopPropagation();
      } else {
        e.stopPropagation();
      }
      if (selectedLessonId === lesson.id) {
        setSelectMaterial(false);
        setSelectedLessonId(undefined);
        setLesson(undefined);
      } else {
        setSelectMaterial(true);
        setSelectedLessonId(lesson.id);
      }
    },
    [lesson, selectedLessonId, setLesson],
  );

  const handleDownloadLessonMaterial = useCallback(
    (content: Content) => {
      handleDownloadMaterial?.(content, lesson);
    },
    [lesson, handleDownloadMaterial],
  );

  const handlePrintMaterial = useCallback(
    (content: Content) => {
      handleDownloadMaterial?.(content, lesson, true);
    },
    [lesson, handleDownloadMaterial],
  );

  const requiredMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === true,
    );
  }, [lesson?.materials]);

  const optionalMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === false,
    );
  }, [lesson?.materials]);

  const lessonMaterials = useMemo(() => {
    return (
      <div className={materialViewStyle}>
        <Collapse isOpened={selectMaterial && selectedLessonId === lesson.id}>
          <div className={styles.materialContents}>
            <div className={styles.subView}>
              {hasArrayValues(requiredMaterials) && (
                <div className={styles.materialSubContents}>
                  <div className={styles.requireTitle}>
                    Required
                    <LordIcon
                      src={IconInfo}
                      isHover={true}
                      colors={{ primary: "#FFB073", secondary: "#FFB073" }}
                      size={20}
                    />
                  </div>
                  <div className={styles.unitsDiv}>
                    {requiredMaterials.map((material, index) => {
                      return (
                        <MaterialCard
                          key={index}
                          content={material}
                          handleDownloadMaterial={handleDownloadLessonMaterial}
                          handlePrintMaterial={handlePrintMaterial}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {hasArrayValues(optionalMaterials) && (
                <div className={styles.materialSubContents}>
                  <div className={styles.optionTitle}>
                    Optional
                    <LordIcon
                      src={IconInfo}
                      isHover={true}
                      colors={{ primary: "#6C969A", secondary: "#6C969A" }}
                      size={20}
                    />
                  </div>
                  <div className={styles.unitsDiv}>
                    {optionalMaterials.map((material, index) => {
                      return (
                        <MaterialCard
                          key={index}
                          content={material}
                          handleDownloadMaterial={handleDownloadLessonMaterial}
                          handlePrintMaterial={handlePrintMaterial}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>
    );
  }, [
    lesson,
    selectedLessonId,
    selectMaterial,
    materialViewStyle,
    requiredMaterials,
    optionalMaterials,
    handleDownloadLessonMaterial,
    handlePrintMaterial,
  ]);

  const statusContent = useMemo(() => {
    return (
      <>
        <div
          className={styles.normalConnectView}
          onClick={(e) => e.stopPropagation()}
        >
          {isHovered && isRequire ? (
            <DisconnectIcon height={42} />
          ) : (
            <MisconnectIcon height={42} />
          )}
        </div>
        <div
          className={styles.preRequestView}
          onClick={(e) => e.stopPropagation()}
          onMouseOver={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          {
            <div
              className={
                isHovered
                  ? isRequire
                    ? styles.preRequestActiveSubView
                    : styles.preRequestHoverSubView
                  : preRequestStyle
              }
            >
              Pre-requisite:&nbsp;
              <div
                className={
                  isHovered && isRequire
                    ? styles.preRequestActiveSubtitle
                    : styles.preRequestSubtitle
                }
              >
                Lesson {preLessonNum}
              </div>
            </div>
          }
        </div>
      </>
    );
  }, [isHovered, preRequestStyle, preLessonNum, handleHover, isRequire]);

  const statusHoverContent = useMemo(() => {
    if (completeRequire && !nextLesson?.prerequisite) {
      if (status === "completed") {
        return (
          <>
            <div
              className={styles.inCompleteConnectView}
              onClick={(e) => e.stopPropagation()}
            >
              <InCompleteConnectIcon width={20} height={39} />
            </div>
          </>
        );
      } else {
        return (
          <>
            <div
              className={styles.inCompleteConnectView}
              onClick={(e) => e.stopPropagation()}
            >
              <CompleteConnectIcon width={20} height={39} />
            </div>
          </>
        );
      }
    } else {
      if (status === "completed" && isHovered && !nextLesson?.prerequisite) {
        return (
          <>
            <div
              className={completedPathStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <ConnectIcon width={20} height={39} />
            </div>
          </>
        );
      }
    }
  }, [completeRequire, status, isHovered, completedPathStyle, nextLesson]);

  const markLessonAsComplete = useCallback(
    async (e: any) => {
      if (e.domEvent) {
        e.domEvent.stopPropagation();
      } else {
        e.stopPropagation();
      }
      if (
        lesson?.id &&
        !loading &&
        !heaLessonCompleteInProgress &&
        !getHeaLessonStatusLoading
      ) {
        if (isHomeEDApp) {
          if (!studyPlan?.id) {
            return;
          }
          const res = await updateHeaLessonComplete({
            variables: {
              input: {
                studyplanId: studyPlan?.id,
                unitId,
                lessonId: lesson.id,
                status: complete
                  ? LessonStatus.NotStarted
                  : LessonStatus.Confirmed,
              },
            },
          });
          if (res?.data?.heaUpdateLessonStatus) {
            getHeaLessonCompletedStatus?.();
            successToast("Lesson Status has been Updated Successfully!");
          }
        } else {
          const res = await updateLessonComplete({
            variables: {
              input: {
                unitId,
                lessonId: lesson.id,
                status: complete
                  ? LessonStatus.NotStarted
                  : LessonStatus.Completed,
              },
            },
          });
          if (res?.data?.updateLessonCompleteStatus) {
            successToast("Lesson Status has been Updated Successfully!");
            if (complete) {
              removeFeedbackStatusWithLesson(lesson.id);
            } else {
              setFeedbackStatusWithLesson(lesson.id, "Enable");
            }
          }
        }
      }
    },
    [
      lesson.id,
      loading,
      heaLessonCompleteInProgress,
      getHeaLessonStatusLoading,
      studyPlan?.id,
      unitId,
      complete,
      successToast,
      updateLessonComplete,
      updateHeaLessonComplete,
      getHeaLessonCompletedStatus,
      setFeedbackStatusWithLesson,
      removeFeedbackStatusWithLesson,
    ],
  );

  const handleVisible = useCallback((visible: boolean) => {
    setShowMenu(visible);
    setShowExitTicketMenu(false);
  }, []);

  const handleExitTicketVisible = useCallback((visible: boolean) => {
    setShowMenu(false);
    setShowExitTicketMenu(visible);
  }, []);

  const handleShowShareModal = (e: any) => {
    e.stopPropagation();
    handleOpenShareModal(lesson);
  };

  const handleClickLessonForm = (e: any) => {
    e.stopPropagation();
    if (isNewLessonLayout) {
      handleStartLesson();
    }
  };

  return (
    <div
      className={`${styles.lessonContainer} ${complete ? styles.completed : incompleted ? styles.inReview : ""}`}
      onClick={() => isParentApp && openPreview(lesson)}
    >
      <Dropdown
        menu={{ items }}
        trigger={
          isParentApp || isNewLessonLayout ? [] : ["contextMenu", "click"]
        }
        onOpenChange={handleVisible}
        open={showMenu}
      >
        <div
          className={
            isHovered
              ? isRequire
                ? styles.prerequisiteHoverCard
                : styles.hoverCard
              : styles.card
          }
          onMouseOver={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
          onClick={handleClickLessonForm}
        >
          <div className={topBorderStyle}></div>
          <div className={bottomBorderStyle}></div>
          <div className={styles.lessonNumView}>
            <div className={lessonNumStyle}>{number}</div>
          </div>
          <div
            className={imageStyle}
            style={{
              backgroundImage: `url(${image ? getMediaAbsolutePath(image) : ""})`,
              position: "relative",
            }}
          >
            {sensitive && (
              <div className={styles.sensitiveView}>
                <div className={styles.sensitiveButton}>
                  {"SENSITIVE CONTENT"}
                  <Tooltip
                    placement="topLeft"
                    title={
                      <div className={styles.sensitiveTooltipText}>
                        {sensitiveTooltip ??
                          "This Lesson contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material."}
                      </div>
                    }
                    arrowPointAtCenter
                    color="#FFFFFF"
                  >
                    <WarningIcon width={13} className={styles.rightTipIcon} />
                  </Tooltip>
                </div>
              </div>
            )}
            {complete ? (
              <MarkCheckIcon className={styles.normal} width={34} height={34} />
            ) : (
              <div>
                <Button
                  label={"View"}
                  className={styles.startButton}
                  onClick={() => isParentApp && openPreview(lesson)}
                />
                {inprogress && !completeRequire ? (
                  <InprogressIcon
                    className={styles.inprogressBadge}
                    width={34}
                    height={34}
                  />
                ) : null}
              </div>
            )}
            {complete || (inprogress && progress && progress > 0) ? (
              <div className={styles.progressBackBar}>
                <div
                  className={styles.progressBar}
                  style={{
                    width: `${complete ? (completeRequire ? 0 : 100) : completeRequire ? 100 : inprogress ? progress : 0}%`,
                    backgroundColor: complete
                      ? "#62C590"
                      : completeRequire
                        ? "#62C590"
                        : "#62C590",
                    borderBottomRightRadius: complete ? "3px" : "0",
                  }}
                ></div>
              </div>
            ) : null}
          </div>
          <section className={styles.content}>
            <div className={styles.head}>
              <div className={styles.title}>{name}</div>
              <div className={styles.duration}>
                {duration ? `${duration}m` : ""}
              </div>
            </div>
            <div className={styles.description}>
              <div className={styles.subheading}>
                {descriptionHeading ?? "This lesson helps students:"}
              </div>
              <ul className={styles.boxView}>
                {(box ?? []).map((content, index) => {
                  const optionStr: string = content as string;
                  return (
                    <li key={index} className={styles.questionBox}>
                      {optionStr}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={styles.exitTicketView}>
              {
                <>
                  {lesson.status === LessonStatus.Completed ? (
                    <div
                      onMouseOver={() => setRateHover(true)}
                      onMouseLeave={() => setRateHover(false)}
                    >
                      <Tooltip
                        placement="topRight"
                        title={
                          <div className={styles.shareTip}>
                            Please click the button to share your thoughts on
                            the Lessons.
                          </div>
                        }
                        arrowPointAtCenter
                        overlayStyle={{ maxWidth: "529px" }}
                        color="#FFFFFF"
                      >
                        <div
                          className={feedbackButtonStyle}
                          onClick={handleOpenFeedback}
                        >
                          <LordIcon
                            src={IconRateLesson}
                            isHover={true}
                            colors={{
                              primary: rateHover ? "#FFB073" : "#6BC690",
                              secondary: rateHover ? "#FFB073" : "#6BC690",
                            }}
                            size={24}
                            target=".LessonForm_feedbackButton__hWzzy"
                          />
                          <span>Rate Lesson</span>
                        </div>
                      </Tooltip>
                    </div>
                  ) : null}
                  {exitTicket?.formData && exitTicket?.status && (
                    <Dropdown
                      menu={{ items: ticketActiveMenu }}
                      trigger={["click"]}
                      placement="bottomRight"
                      onOpenChange={handleExitTicketVisible}
                      open={showExitTicketMenu}
                    >
                      <Button
                        disabled={canceling || feedbackLoading}
                        label=""
                        size="small"
                        className={styles.activeButton}
                        onClick={(e) => e.stopPropagation()}
                        leftIcon={
                          canceling || feedbackLoading ? (
                            <Spinner width={20} height={20} />
                          ) : (
                            <TicketIcon />
                          )
                        }
                      />
                    </Dropdown>
                  )}
                  {/* <Dropdown menu={{ items: exitTicket?.formData ? (exitTicket?.status ? ticketActivatedMenu : ticketGenerateMenu) : normalMenu }} trigger={['click']} placement="bottomRight" onOpenChange={handleExitTicketActiveVisible} open={showExitTicketActiveMenu}>
                <Button disabled={canceling || feedbackLoading} label='' size='small' className={burgerButtonStyle} onClick={e => e.stopPropagation()} leftIcon={canceling || feedbackLoading ? <Spinner width={20} height={20} /> : <BurgerIcon />} />
              </Dropdown> */}
                  <div>
                    <Tooltip
                      placement="topRight"
                      title={
                        <div className={styles.shareTip}>Share Lesson</div>
                      }
                      arrowPointAtCenter
                      color="#FFFFFF"
                    >
                      <div
                        className={shareButtonStyle}
                        onClick={handleShowShareModal}
                      >
                        <ShareIcon width={26} height={26} />
                      </div>
                    </Tooltip>
                  </div>
                  <Menu items={burgeritems} mode="horizontal" />
                </>
              }
            </div>
          </section>
          {lesson.materials && lesson.materials?.length > 0 && (
            <div className={styles.materialView}>
              <Button
                size="small"
                className={styles.materialButton}
                label="DOWNLOAD LESSON MATERIALS"
                leftIcon={
                  <Tooltip
                    placement="topLeft"
                    title={
                      <div className={styles.sensitiveTooltipText}>
                        {"Download the Lesson Materials"}
                        <br /> {"before beginning the Lesson"}
                      </div>
                    }
                    arrowPointAtCenter
                    color="#FFFFFF"
                  >
                    <IconOutline className={styles.infoIcon} width={19} />
                  </Tooltip>
                }
                rightIcon={
                  <IconArrowUp
                    className={[
                      styles.arrowIcon,
                      selectMaterial && selectedLessonId === lesson.id
                        ? styles.materialToggleClose
                        : styles.materialToggleOpen,
                    ].join(" ")}
                    width={13}
                  />
                }
                onClick={handleSelectMaterials}
              />
            </div>
          )}
          {
            <>
              <div
                className={styles.statusTipView}
                style={{
                  display: completeRequire ? "flex" : "none",
                  right: "-78px",
                }}
              >
                <div className={styles.tipTextView}>
                  <div className={styles.tipTitle}>
                    {complete
                      ? "Mark Lesson as Incomplete"
                      : "Mark Lesson as Complete"}
                  </div>
                </div>
                <div className={styles.arrowDown}></div>
              </div>
              <div className={styles.statusView}>
                <button
                  className={completedCheckStyle}
                  onClick={markLessonAsComplete}
                  onMouseOver={() => setCompleteRequire(true)}
                  onMouseLeave={() => setCompleteRequire(false)}
                >
                  {loading ||
                  heaLessonCompleteInProgress ||
                  fetching ||
                  fetchingHea ? (
                    <LoadingOutlined
                      style={{ fontSize: 24, borderRadius: 15 }}
                      spin
                    />
                  ) : complete ? (
                    <>
                      <MarkCheckIcon
                        className={styles.completeCheckIcon}
                        width={32}
                        height={32}
                      />
                      <MarkCrossIcon
                        className={styles.inCompletedCrossIcon}
                        width={32}
                        height={32}
                      />
                    </>
                  ) : (
                    <>
                      {completeRequire ? (
                        <MarkCheckIcon
                          className={styles.completedCrossIcon}
                          width={32}
                          height={32}
                        />
                      ) : (
                        <NormalCheckIcon
                          className={styles.normalCheckIcon}
                          width={32}
                          height={32}
                        />
                      )}
                    </>
                  )}
                </button>
              </div>
            </>
          }
        </div>
      </Dropdown>
      {
        <>
          {lesson.materials && lesson.materials?.length > 0
            ? lessonMaterials
            : null}
          {index !== 0 && !prerequisite ? statusContent : null}
          {index !== lessonCount - 1 && statusHoverContent}
        </>
      }
    </div>
  );
}

export default NewLayoutLessonForm;
